import { useRouter } from "next/router";
import { useModalHook } from "../../../services/hooks/useModal";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../services/hooks/useReduxHooks";
import { useTranslationI18 } from "../../../services/hooks/useTranslationI18";
import {
  redeemOfferAction,
  removeOfferAction,
} from "../../../services/redux/slices/cartLoyaltyV2Slice";
import {
  CUSTOMER_ANALYTICS_EVENT,
  OFFER_CATEGORY,
  PagesEnum,
} from "../../../utils/enums";
import * as textPreparation from "../../../utils/help/textPreparation/textPreparation";
import Offer from "../../../utils/OfferClass/Offer/Offer";
import { OFFER_SYSTEM, TimedOfferDetailsType } from "../../../utils/types";
import {
  LineDotsReferral,
  PaperPlaneIcon,
} from "../../screens/S-Referral/icons";
import { GeneralOfferIcon } from "../GeneralOfferIcon/GeneralOfferIcon";
import { BottomRightCornerWave } from "../Icons/Icons";
import styles from "./FlashOfferBanner.module.scss";

type FlashOfferBannerProps = {
  offer: Offer;
  offersTab?: boolean;
  /** Sizes the copy as the tiers design does, which sets the banner's height. */
  tierSized?: boolean;
};

export default function FlashOfferBanner({
  offer,
  offersTab,
  tierSized,
}: FlashOfferBannerProps) {
  const { t } = useTranslationI18();
  const router = useRouter();

  const organizationCustomer = useAppSelector(
    (state) => state.partnerAccount.organizationCustomer
  );
  const restaurantInfo = useAppSelector((state) => state.config.restaurantInfo);
  const cartAndLoyaltyReducer = useAppSelector(
    (state) => state.cartAndLoyaltyReducer
  );
  const cartLoyaltyV2 = useAppSelector((state) => state.cartLoyaltyV2Reducer);
  const itemStatusRedux = useAppSelector((state) => state.itemStatus);
  const orderTypeParam = useAppSelector(
    (state) => state.queryParams.orderTypeParam
  );
  const redeemedOffer = cartLoyaltyV2.cart.redeemedOffer;

  const promoCode = cartAndLoyaltyReducer.promoCode;

  const dispatch = useAppDispatch();

  const { handleModal } = useModalHook();

  const expDate = t("offers:exp-date");
  const remove = textPreparation.capitalizeFirstLetter(t("offers:remove"));
  const apply = textPreparation.capitalizeFirstLetter(t("offers:apply"));
  const beenReferredI18n = t("offers:been-referred");
  const from = t("common:from");
  const to = t("common:to");

  const disable = !offer.isOfferApplicable({
    itemStatusRedux,
    orderType: orderTypeParam,
    cart: cartLoyaltyV2.cart,
    timeZone: restaurantInfo?.timeZone!,
  });
  const addTwist = offer.twistOfferIsAvailable(
    cartAndLoyaltyReducer.isTwistOfferAvailable
  );

  const toggleModal = (offer: Offer, dismiss?: boolean) => {
    if (offer.isItemFromListOffer()) {
      handleModal(true, "ItemFromList/ItemFromList", {
        componentProps: { offer: offer },
      });

      return;
    }
    if (!dismiss) {
      const isFlashOffer =
        offer.offerSystem !== OFFER_SYSTEM.POINTING_OFFER &&
        (offer.isTimedOffer() ||
          offer.offerSystem === OFFER_SYSTEM.FIRST_TIME_OFFER);
      const applyEvent = router.pathname.includes(PagesEnum.Cart)
        ? isFlashOffer
          ? CUSTOMER_ANALYTICS_EVENT.APPLY_FLASH_OFFER_FROM_CART
          : CUSTOMER_ANALYTICS_EVENT.APPLY_REWARD_FROM_CART
        : offersTab
        ? isFlashOffer
          ? CUSTOMER_ANALYTICS_EVENT.APPLY_FLASH_OFFER_FROM_REWARDS_PAGE
          : CUSTOMER_ANALYTICS_EVENT.APPLY_REWARD_FROM_REWRADS_PAGE
        : CUSTOMER_ANALYTICS_EVENT.APPLY_FLASH_OFFER_FROM_BANNER;

      dispatch(
        redeemOfferAction({
          offer,
          applyEvent,
          applyExtra: {
            offerId: offer.id,
            offerType: offer.offerType,
            offerSystem: offer.offerSystem,
          },
        })
      );
    }
  };

  const handleRemoveOffer = () => {
    dispatch(removeOfferAction());
  };

  const createMarkup = (html: string) => {
    return { __html: html };
  };

  const isOfferExpiryDateLessThan60Days = (offer: Offer) => {
    return (
      offer.expiryDate &&
      new Date(offer.expiryDate) <=
        new Date(new Date().setDate(new Date().getDate() + 60))
    );
  };

  const extraPadding =
    offer.isMultiplierOffer() || offer.isBogoOffer() || offer.isBundleOffer();

  const offerSubtext = Offer.getLocalizedValue(offer.uiDetails?.subText);

  return (
    <div
      data-testid={`offer_slider_${offer.offerType}`}
      className={`${styles.slide} ${
        organizationCustomer?.verified && styles.organization_offer
      } ${offer.isEqual(redeemedOffer) && styles.active_slide} ${"bg__12"} ${
        disable ? styles.unavailable : ""
      } ${extraPadding && !offersTab && styles.extra_margin} ${
        tierSized ? styles.tier_sized : ""
      } rounded`}
    >
      {promoCode.packageOffer &&
        offer.isEqual(promoCode.packageOffer) &&
        promoCode.referralCode && (
          <div className={styles.line_dots_cotainer}>
            <span className={styles.line_dots}>
              <LineDotsReferral />
            </span>
          </div>
        )}
      <div
        className={`${styles.slide_wrapper} ${
          extraPadding && styles.extra_padding
        }`}
      >
        <div className={styles.slide_details}>
          <GeneralOfferIcon
            offer={offer}
            sliderStyle
            backgroundIconColor="primary"
            twist={addTwist}
          />

          <div className={`${styles.offer_details}`}>
            <div className={styles.offer_action}>
              {!offer.isEqual(redeemedOffer) ? (
                !disable && (
                  <button
                    className={styles.offer_action__btn}
                    onClick={() => toggleModal(offer, undefined)}
                    data-testid={`apply_offer_${offer.offerType}`}
                  >
                    {apply}
                  </button>
                )
              ) : (
                <>
                  {!(
                    promoCode.packageOffer &&
                    offer.isEqual(promoCode.packageOffer) &&
                    promoCode.referralCode
                  ) ? (
                    <button
                      className={`${styles.offer_action__btn} ${styles.remove_btn}`}
                      data-testid={`remove_offer_${offer.offerType}`}
                      onClick={() => {
                        handleRemoveOffer();
                      }}
                    >
                      {remove}
                    </button>
                  ) : (
                    <span className={styles.paper_plane}>
                      <PaperPlaneIcon />
                    </span>
                  )}
                </>
              )}
            </div>
            <p
              className={styles.offer_title}
              dangerouslySetInnerHTML={createMarkup(
                Offer.getLocalizedValue(offer.uiDetails?.titleText) || ""
              )}
            />
            {disable && (
              <div
                data-testid="slider-offer-unavailable"
                className={styles.unavailable}
              >
                {t(
                  offer.isItemOffer() ||
                    offer.offerCategory === OFFER_CATEGORY.ITEM_OFFER
                    ? offer.isBundleOffer()
                      ? "offers:bundle-unavailable"
                      : "common:items-unavailable"
                    : "offers:offer-unavailable"
                )}
              </div>
            )}
            <p className={styles.offer_desc_wrapper}>
              {offerSubtext && (
                <p className={styles.offer_desc}>
                  {promoCode.packageOffer &&
                  offer.isEqual(promoCode.packageOffer) &&
                  promoCode.referralCode ? (
                    beenReferredI18n
                  ) : (
                    <>
                      <span
                        dangerouslySetInnerHTML={createMarkup(
                          offerSubtext || ""
                        )}
                      />
                    </>
                  )}
                </p>
              )}

              {disable && offer.isTimedOffer() && (
                <p className={styles.offer_desc}>
                  <b>
                    {`${from} ${textPreparation.formatTimeAMPMLocalized(
                      t,
                      (offer.details as TimedOfferDetailsType).from
                    )} ${to} ${textPreparation.formatTimeAMPMLocalized(
                      t,
                      (offer.details as TimedOfferDetailsType).to
                    )}`}
                  </b>
                </p>
              )}
              {offer.expiryDate && isOfferExpiryDateLessThan60Days(offer) && (
                <p
                  className={styles.expiry_date}
                  data-testid={`${offer.offerType}-expiryDate`}
                >
                  <span>{expDate} </span>
                  {textPreparation.getDateForamt(
                    offer.expiryDate,
                    "MMM DD, YYYY"
                  )}
                </p>
              )}
            </p>
          </div>

          <div className={styles.slider_bottom_svg}>
            <BottomRightCornerWave />
          </div>
        </div>
      </div>
    </div>
  );
}
